import React, { useState } from 'react';
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from 'framer-motion';
import { HiOutlineHome, HiOutlineUser, HiOutlineCode, HiOutlineBriefcase, HiOutlineAcademicCap, HiOutlineMail } from 'react-icons/hi';
import { useLenis } from './LenisProvider'; // Import the Lenis hook

const navLinks = [
  { name: "Home", href: "#home", icon: HiOutlineHome },
  { name: "About", href: "#about", icon: HiOutlineUser },
  { name: "Skills", href: "#skills", icon: HiOutlineCode },
  { name: "Projects", href: "#projects", icon: HiOutlineBriefcase },
  { name: "Education", href: "#education", icon: HiOutlineAcademicCap },
  { name: "Contact", href: "#contact", icon: HiOutlineMail },
];

const DockNavbar = () => {
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hidden, setHidden] = useState(false);
  const { scrollY } = useScroll();
  const lenisRef = useLenis(); // Get the Lenis instance from our provider

  // Logic to hide/show navbar on scroll
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious();
    if (latest > previous && latest > 150) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    if (latest < 150) {
        setHidden(false);
    }
  });

  // Click handler to trigger Lenis smooth scroll
  const handleLinkClick = (e, target) => {
    e.preventDefault();
    lenisRef.current?.scrollTo(target, { duration: 1.5 });
  };

  const dockVariants = {
    visible: { y: 0, opacity: 1, transition: { duration: 0.3, ease: "easeOut" } },
    hidden: { y: "120%", opacity: 0, transition: { duration: 0.3, ease: "easeIn" } },
  };

  return (
    <motion.div
      className="fixed bottom-10 left-0 right-0 flex h-16 items-center justify-center z-50"
      variants={dockVariants}
      animate={hidden ? "hidden" : "visible"}
      onHoverStart={() => setHidden(false)}
    >
      <div
        onMouseLeave={() => setHoveredLink(null)}
        className="flex h-full items-center gap-4 rounded-2xl bg-black/20 px-4 backdrop-blur-xl border border-white/10 shadow-lg shadow-black/20"
      >
        {navLinks.map((link) => (
          <AppIcon 
            key={link.name} 
            href={link.href} 
            name={link.name} 
            setHoveredLink={setHoveredLink}
            onClick={(e) => handleLinkClick(e, link.href)} // Pass the click handler
          >
            <link.icon className="w-6 h-6" />
          </AppIcon>
        ))}
      </div>
       <AnimatePresence>
        {hoveredLink && (
            <motion.div
                className="absolute bottom-full mb-2 px-2 py-1 bg-primary rounded-md text-xs text-text-primary whitespace-nowrap shadow-lg"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
            >
                {hoveredLink}
            </motion.div>
        )}
       </AnimatePresence>
    </motion.div>
  );
};

const AppIcon = ({ href, name, setHoveredLink, onClick, children }) => {
  return (
    <motion.a
      href={href}
      className="block p-2 text-text-secondary transition-colors hover:text-text-primary"
      onHoverStart={() => setHoveredLink(name)}
      onClick={onClick} // Attach the handler to the link
      whileHover={{ scale: 1.2 }}
      whileTap={{ scale: 0.9 }}
    >
      {children}
    </motion.a>
  );
};

export default DockNavbar;